import { useEffect, useRef, useState, useMemo } from "react";
import "../pages/conv-page.css";

const API_BASE = "http://localhost:4000";

export default function PersonalConversation({ conversationId, title, createdAt }) {
  const [messages, setMessages] = useState([]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [session, setSession] = useState(null);
  
  const [liveStatus, setLiveStatus] = useState("Locked");
  const [ending, setEnding] = useState(false);

  const [error, setError] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const bottomRef = useRef(null);
  const socketRef = useRef(null);

  const canEndSession = useMemo(() => {
    return !!session && !session.ended_at;
  }, [session]);

  //console.log("PersonalConversation render", { session});

  async function handleEndSession() {
    setEnding(true);
    try {
      // add your end-chat API call here
    } finally {
      setEnding(false);
    }
  }

  useEffect(() => {
    if (!conversationId) return;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/personal/${conversationId}`, {
          credentials: "include"
        });
        if (!res.ok) throw new Error("Failed to load conversation");
        const data = await res.json();
        setSession(data);
        setMessages(data.messages || []);
      } catch (err) {
        setError(err.message || "Failed to load conversation");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    let socket;

    async function connect() {
      try {
        const mod = await import("socket.io-client");
        if (cancelled) return;
        const io = mod.io || mod.default;
        socket = io(API_BASE, {
          path: "/backend/socket.io",
          transports: ["websocket", "polling"],
        });
        socketRef.current = socket;

        socket.on("connect", () => {
          socket.emit("join_personal", conversationId);
        });

        socket.on("personal_message", (msg) => {
          if (msg.sender === "system") setIsTyping(false);
          setMessages(prev => {
            const exists = prev.some(m => m.id === msg.id);
            return exists ? prev : [...prev, msg];
          });
        });
        socket.on("session_ended", () => { setLiveStatus("Session ended"); setSession(prev => prev ? { ...prev, ended_at: prev.ended_at || new Date().toISOString() } : prev); });
      } catch {}
    }

    connect();
    return () => {
      cancelled = true;
      socketRef.current = null;
      if (socket) socket.disconnect();
    };
  }, [conversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  async function handleSend(e) {
  e.preventDefault();
  console.log("[send] handler entered", note);

  if (!note.trim() || sending) {
    console.log("[send] blocked");
    return;
  }

  const userMessage = note.trim();
  setSending(true);
  setNote("");
  setIsTyping(true);

  try {
    console.log("[send] posting message");
    const res = await fetch(`${API_BASE}/api/personal/${conversationId}/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ message: userMessage })
    });
    console.log("[send] post returned", res.status);

    if (!res.ok) throw new Error("Send failed");

    const refreshRes = await fetch(`${API_BASE}/api/personal/${conversationId}`, {
      credentials: "include"
    });
    const data = await refreshRes.json();
    console.log("[send] refreshed messages", data.messages?.length);

    setMessages(data.messages || []);
    setIsTyping(false);
  } catch (err) {
    console.error("[send] error", err);
    setNote(userMessage);
    setIsTyping(false);
  } finally {
    setSending(false);
  }
}

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  }

  if (loading) {
    return (
      <div className="conv-shell">
        <div className="conv-card">Loading conversation…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conv-shell">
        <div className="conv-card conv-error">
          <h1>Conversation unavailable</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      })
    : null;

  const userMessageCount = messages.filter(m => m.sender === "user").length;

  return (
    <div className="conv-shell">
      <main className="conv-card">
        <header className="conv-header">
          <div>
            <h1>💬 {title || "Conversations"}</h1>
            <p className="conv-subtitle">
              A private space to reflect, explore, and be heard.
            </p>
          </div>
          <div className="conv-status live">Active</div>
        </header>

        <section className="conv-grid">
          <div className="conv-panel chat-panel chat-block" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div
              className="conv-panel-head"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "16px",
                flexWrap: "wrap"
              }}
            >
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, flexShrink: 0 }}>Conversations</h2>
                <p className="conv-muted" style={{ marginBottom: 0 }}>
                  Write freely. Responses are drawn from your journey patterns.
                </p>
              </div>

              {canEndSession && (
                <button
                  className="conv-end-btn"
                  onClick={handleEndSession}
                  disabled={ending}
                  style={{ flexShrink: 0 }}
                >
                  {ending ? "Ending…" : "End Chat"}
                </button>
              )}
            </div>

            <div className="chat-messages" style={{ flex: "1 1 auto", overflowY: "auto" }}>
              {messages.length === 0 && !isTyping && (
                <p style={{ opacity: 0.4, fontSize: "13px", textAlign: "center", marginTop: "24px" }}>
                  Nothing yet. Start the conversation below.
                </p>
              )}

              {messages.map((msg) => {
                const isUser = msg.sender === "user";
                return (
                  <div key={msg.id || msg.sent_at} className={`chat-bubble ${isUser ? "mine" : "theirs"}`}>
                    <div
                      style={{
                        fontSize: "11px",
                        opacity: 0.5,
                        marginBottom: "4px",
                        color: isUser ? "#7dd3e8" : "#b47de8",
                      }}
                    >
                      {isUser ? "You" : "Journey Trails"}
                    </div>
                    <div className="chat-text">{msg.message}</div>
                    <div className="chat-time">
                      {new Date(msg.sent_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </div>
                  </div>
                );
              })}

              {isTyping && (
                <div className="chat-bubble theirs" style={{ opacity: 0.5 }}>
                  <div style={{ fontSize: "11px", opacity: 0.5, marginBottom: "4px", color: "#b47de8" }}>
                    Journey Trails
                  </div>
                  <div className="chat-text" style={{ fontStyle: "italic" }}>Reflecting…</div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            <form onSubmit={handleSend} style={{ marginTop: "8px", flexShrink: 0 }}>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write a message…"
                rows={3}
                className="chat-input"
                disabled={sending}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                <button
                  type="submit"
                  disabled={sending || !note.trim()}
                  className="chat-send-btn"
                >
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          </div>

          <div className="conv-panel">
            <h2>Details</h2>

            <dl className="conv-dl">
              {title && <div><dt>Title</dt><dd>{title}</dd></div>}
              {formattedDate && <div><dt>Started</dt><dd>{formattedDate}</dd></div>}
              <div><dt>Messages</dt><dd>{userMessageCount}</dd></div>
            </dl>

            <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <button
                type="button"
                onClick={() => window.open(`/new?conversation_id=${conversationId}`, "_blank")}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "10px",
                  border: "1px solid rgba(125,211,232,0.3)",
                  background: "rgba(125,211,232,0.08)",
                  color: "#7dd3e8",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                📓 Write Journal Entry ↗
              </button>
              <p style={{ fontSize: "11px", opacity: 0.45, marginTop: "6px", textAlign: "center", lineHeight: 1.5 }}>
                Opens in a new tab. Your conversation context will be available.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}